import React from 'react';
import { Upload, Camera, Sparkles, Loader2, X } from 'lucide-react';

const ImageUploadSection = ({ 
  imagePreview, 
  onImageChange, 
  onTakePhoto, 
  onEnhanceImage, 
  onImageDelete,
  profileImageFile,
  isEnhancing,
  isLoadingExistingImage = false,
  isCameraOpen = false,
  aiResults = null, // AI 결과 상태 추가
  enhancementProgress = { percent: 0, message: '' } // 진행률 추가
}) => {
  return (
    <div className="mb-4">
      <label className="label">프로필 사진</label>
      
      {/* 이미지 프리뷰 영역 */}
      <div className="flex flex-col items-center mb-3">
        <div className="relative">
          <div className={`w-40 h-40 rounded-full flex items-center justify-center overflow-hidden transition-all duration-200 ${
            imagePreview 
              ? 'border-2 border-blue-300 shadow-lg' 
              : 'border-2 border-dashed border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
          }`}>
            {imagePreview ? (
              <img 
                src={imagePreview} 
                alt="Profile Preview" 
                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                onClick={() => {/* 모달 열기 로직은 부모 컴포넌트에서 처리 */}}
                title="클릭하여 크게 보기"
              />
            ) : (
              <div className="text-center">
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500 px-3">이미지를 업로드하거나<br/>사진을 촬영해주세요</p>
              </div>
            )}
          </div>
          
          {/* 삭제 버튼 (이미지가 있을 때만 표시) */}
          {imagePreview && (
            <button 
              onClick={onImageDelete}
              className="btn btn-danger-ghost absolute -top-1 -right-1 w-7 h-7 p-0 rounded-full shadow-sm"
              title="이미지 삭제"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* 액션 버튼들 */}
      <div className="grid grid-cols-3 gap-2">
        {/* 이미지 업로드 카드 */}
        <div className={`card card-sm p-2 sm:p-3 transition-all duration-200 ${
          isLoadingExistingImage 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:border-blue-300'
        }`}>
          <input 
            type="file" 
            id="profile-image-upload" 
            className="hidden" 
            accept="image/*" 
            onChange={onImageChange}
            disabled={isLoadingExistingImage}
          />
          <label 
            htmlFor={isLoadingExistingImage ? '' : 'profile-image-upload'} 
            className={`flex flex-col items-center text-center ${
              isLoadingExistingImage ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1 sm:mb-2 ${
              isLoadingExistingImage ? 'bg-gray-50' : 'bg-blue-50'
            }`}>
              <Upload className={`w-4 h-4 sm:w-5 sm:h-5 ${
                isLoadingExistingImage ? 'text-gray-400' : 'text-blue-600'
              }`} />
            </div>
            <h3 className={`text-xs sm:text-sm font-medium mb-0.5 ${
              isLoadingExistingImage ? 'text-gray-400' : 'text-gray-900'
            }`}>파일 선택</h3>
            <p className={`text-xs hidden sm:block ${
              isLoadingExistingImage ? 'text-gray-400' : 'text-gray-500'
            }`}>JPG, PNG 파일</p>
          </label>
        </div>
        
        {/* 카메라 촬영 카드 */}
        <div className={`card card-sm p-2 sm:p-3 transition-all duration-200 ${
          isLoadingExistingImage 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:border-green-300 cursor-pointer'
        }`}>
          <div 
            onClick={isLoadingExistingImage ? undefined : onTakePhoto}
            className="flex flex-col items-center text-center"
          >
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1 sm:mb-2 ${
              isLoadingExistingImage ? 'bg-gray-50' : 'bg-green-50'
            }`}>
              <Camera className={`w-4 h-4 sm:w-5 sm:h-5 ${
                isLoadingExistingImage ? 'text-gray-400' : 'text-green-600'
              }`} />
            </div>
            <h3 className={`text-xs sm:text-sm font-medium mb-0.5 ${
              isLoadingExistingImage ? 'text-gray-400' : 'text-gray-900'
            }`}>
              사진 촬영
            </h3>
            <p className={`text-xs hidden sm:block ${
              isLoadingExistingImage ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {isCameraOpen ? '카메라 다시 시작' : '웹캠으로 촬영'}
            </p>
          </div>
        </div>
        
        {/* AI 개선 카드 */}
        <div className={`card card-sm p-2 sm:p-3 transition-all duration-200 ${
          (profileImageFile && !aiResults && !isLoadingExistingImage)
            ? 'hover:border-purple-300' 
            : 'opacity-50 cursor-not-allowed'
        }`}>
          <button 
            onClick={onEnhanceImage} 
            disabled={!profileImageFile || !!aiResults || isEnhancing || isLoadingExistingImage}
            className="w-full flex flex-col items-center text-center disabled:cursor-not-allowed"
          >
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1 sm:mb-2 ${
              (profileImageFile && !aiResults && !isLoadingExistingImage) ? 'bg-purple-50' : 'bg-gray-50'
            }`}>
              {isEnhancing ? (
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 animate-spin" />
              ) : (
                <Sparkles className={`w-4 h-4 sm:w-5 sm:h-5 ${
                  (profileImageFile && !aiResults && !isLoadingExistingImage) ? 'text-purple-600' : 'text-gray-400'
                }`} />
              )}
            </div>
            <h3 className={`text-xs sm:text-sm font-medium mb-0.5 ${
              (profileImageFile && !aiResults && !isLoadingExistingImage) ? 'text-gray-900' : 'text-gray-400'
            }`}>
              {isEnhancing ? `AI 개선 중 ${enhancementProgress.percent > 0 ? `${enhancementProgress.percent}%` : ''}` : 'AI 개선'}
            </h3>
            <p className={`text-xs hidden sm:block ${
              (profileImageFile && !aiResults && !isLoadingExistingImage) ? 'text-gray-500' : 'text-gray-400'
            }`}>
              {isEnhancing && enhancementProgress.message
                ? enhancementProgress.message
                : (aiResults 
                  ? 'AI 개선 완료됨' 
                  : ((profileImageFile && !isLoadingExistingImage) ? '화질 향상 및 배경 수정' : '이미지 후 사용')
                )
              }
            </p>
          </button>
        </div>
      </div>
      
      {/* AI 개선 진행률 바 */}
      {isEnhancing && enhancementProgress.percent > 0 && (
        <div className="mt-3 space-y-1">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-purple-500 to-purple-600 h-full transition-all duration-300 ease-out"
              style={{ width: `${enhancementProgress.percent}%` }}
            />
          </div>
          <p className="text-xs text-center text-gray-600 sm:hidden">
            {enhancementProgress.message || 'AI 처리 중...'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploadSection;
